import { Maybe } from '@aibees/shared/definition';
import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import type { DialogActionsProps as MuiDialogActionsProps } from '@mui/material/DialogActions';
import type { DialogContentProps as MuiDialogContentProps } from '@mui/material/DialogContent';
import type { DialogTitleProps as MuiDialogTitleProps } from '@mui/material/DialogTitle';
import { type Theme } from '@mui/material/styles';
import { Padding } from '../../types';

declare module '@mui/material/Dialog' {
  interface DialogProps {
    padding?: Padding;
  }
}

declare module '@mui/material/DialogTitle' {
  interface DialogTitleOwnProps {
    padding?: Padding;
  }
}

declare module '@mui/material/DialogContent' {
  interface DialogContentProps {
    padding?: Padding;
  }
}

declare module '@mui/material/DialogActions' {
  interface DialogActionsProps {
    padding?: Padding;
  }
}

function paddingStyles(padding: Maybe<Padding>, theme: Theme) {
  if (padding === 'normal') {
    return theme.spacing(1.5);
  } else if (padding === 'extra') {
    return theme.spacing(2);
  } else if (padding === 'none') {
    return theme.spacing(0);
  } else return theme.spacing(1.5);
}

export default function Dialog(theme: Theme) {
  const dialogTitleStyles = (
    ownerState: MuiDialogTitleProps & { padding?: Padding }
  ) => {
    const defaultStyle = {
      height: theme.spacing(7),
      fontSize: theme.typography.h6.fontSize,
      padding: paddingStyles(ownerState.padding, theme),
      display: 'flex',
      alignItems: 'center',
    };
    return defaultStyle;
  };

  const dialogContentStyles = (
    ownerState: MuiDialogContentProps & { padding?: Padding }
  ) => {
    const defaultStyle = { padding: paddingStyles(ownerState.padding, theme) };
    return defaultStyle;
  };

  const dialogActionsStyles = (
    ownerState: MuiDialogActionsProps & { padding?: Padding }
  ) => {
    const defaultStyle = {
      padding: paddingStyles(ownerState.padding, theme),
      gap: theme.spacing(1),
      height: theme.spacing(7),
    };
    return defaultStyle;
  };

  const paperStyles = (ownerState: MuiDialogProps) => {
    const xxsSize = ownerState.maxWidth === 'xxs';
    const xsSize = ownerState.maxWidth === 'xs';
    const smSize = ownerState.maxWidth === 'sm';
    const mdPlusSize = ownerState.maxWidth === 'smplus';
    const mdSize = ownerState.maxWidth === 'md';
    const lgSize = ownerState.maxWidth === 'lg';
    const xlSize = ownerState.maxWidth === 'xl';

    const defaultStyle = {
      boxShadow: theme.customShadows.dialog,
      borderRadius: theme.shape.borderRadius * 0.5,
      ...(!ownerState.fullScreen && {
        margin: theme.spacing(2),
      }),
    };

    const sizes = {
      ...(xxsSize && {
        maxWidth: '400px !important',
        width: '400px',
      }),
      ...(xsSize && {
        maxWidth: '480px !important',
        width: '480px',
      }),
      ...(smSize && {
        maxWidth: '600px !important',
        width: '600px',
      }),
      ...(mdPlusSize && {
        maxWidth: '800px !important',
        width: '800px',
      }),
      ...(mdSize && {
        maxWidth: '900px !important',
        width: '900px',
      }),
      ...(lgSize && {
        maxWidth: '1200px !important',
        width: '1200px',
      }),
      ...(xlSize && {
        maxWidth: '1536px !important',
        width: '1536px',
      }),
    };

    return { ...defaultStyle, ...sizes };
  };

  return {
    MuiDialog: {
      styleOverrides: {
        paper: ({ ownerState }: { ownerState: MuiDialogProps }) => {
          return paperStyles(ownerState);
        },
        paperFullScreen: {
          borderRadius: 0,
        },
        root: {},
      },
    },

    MuiDialogTitle: {
      defaultProps: {
        component: 'h6',
        padding: 'normal',
      },
      styleOverrides: {
        root: ({
          ownerState,
        }: {
          ownerState: MuiDialogTitleProps & { padding?: Padding };
        }) => dialogTitleStyles(ownerState),
      },
    },
    MuiDialogContent: {
      defaultProps: {
        dividers: true,
        padding: 'normal',
      },
      styleOverrides: {
        root: ({
          ownerState,
        }: {
          ownerState: MuiDialogContentProps & { padding?: Padding };
        }) => dialogContentStyles(ownerState),
      },
    },
    MuiDialogActions: {
      defaultProps: {
        disableSpacing: true,
        padding: 'normal',
      },
      styleOverrides: {
        root: ({
          ownerState,
        }: {
          ownerState: MuiDialogActionsProps & { padding?: Padding };
        }) => dialogActionsStyles(ownerState),
      },
    },
  };
}
